import React from 'react';
import { 
  BarChart, Bar, 
  PieChart, Pie, Cell, 
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { Box, Typography, Paper, Grid } from '@mui/material';

/**
 * Component for rendering fall risk assessment charts and visualizations
 * @param {Object} props - Component props
 * @param {String} props.chartType - Type of chart to render (bar, pie, line, radar)
 * @param {String} props.title - Chart title
 * @param {String} props.description - Chart description
 * @returns {JSX.Element} - The rendered component
 */
const FallRiskCharts = ({ chartType = 'bar', title, description }) => {
  // Fall risk factors data
  const fallRiskFactorsData = [
    { name: 'Medication Issues', value: 28, fill: '#e57373' },
    { name: 'Balance Problems', value: 24, fill: '#64b5f6' },
    { name: 'Environmental Hazards', value: 21, fill: '#4db6ac' },
    { name: 'Vision Problems', value: 14, fill: '#9575cd' },
    { name: 'Cognitive Impairment', value: 13, fill: '#ffb74d' }
  ];

  // Age-related fall risk data
  const ageRiskData = [
    { age: '65-69', risk: 25 },
    { age: '70-74', risk: 30 },
    { age: '75-79', risk: 35 },
    { age: '80-84', risk: 40 },
    { age: '85+', risk: 50 }
  ];

  // Condition-specific fall risk data
  const conditionRiskData = [
    { name: 'Baseline', mobility: 60, balance: 60, strength: 60, cognition: 60, vision: 60 },
    { name: 'Parkinson\'s', mobility: 30, balance: 25, strength: 40, cognition: 50, vision: 55 },
    { name: 'Arthritis', mobility: 40, balance: 50, strength: 30, cognition: 60, vision: 60 },
    { name: 'Dementia', mobility: 45, balance: 40, strength: 50, cognition: 20, vision: 55 },
    { name: 'Diabetes', mobility: 50, balance: 45, strength: 55, cognition: 55, vision: 30 },
    { name: 'Post-Stroke', mobility: 25, balance: 30, strength: 35, cognition: 40, vision: 50 }
  ];

  // Fall prevention effectiveness data
  const preventionData = [
    { intervention: 'Exercise Programs', effectiveness: 42 },
    { intervention: 'Home Modifications', effectiveness: 39 },
    { intervention: 'Medication Review', effectiveness: 35 },
    { intervention: 'Vision Correction', effectiveness: 30 },
    { intervention: 'Vitamin D Supplement', effectiveness: 25 },
    { intervention: 'Education Only', effectiveness: 15 }
  ];

  // COLORS for pie chart
  const COLORS = ['#e57373', '#64b5f6', '#4db6ac', '#9575cd', '#ffb74d'];

  // Render the appropriate chart based on chartType
  const renderChart = () => {
    switch(chartType) {
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={fallRiskFactorsData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={150}
                fill="#8884d8"
                dataKey="value"
              >
                {fallRiskFactorsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={450}>
            <BarChart
              data={preventionData}
              margin={{ top: 20, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="intervention" angle={-45} textAnchor="end" height={80} />
              <YAxis 
                label={{ 
                  value: 'Reduction in Fall Risk (%)', 
                  angle: -90, 
                  position: 'insideLeft',
                  offset: -45,
                  style: { textAnchor: 'middle' }
                }} 
              />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend 
                verticalAlign="bottom" 
                height={36} 
                wrapperStyle={{
                  paddingTop: 20,
                  marginTop: 10,
                  bottom: 0
                }}
              />
              <Bar dataKey="effectiveness" name="Effectiveness (%)" fill="#64b5f6" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={ageRiskData}
              margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis 
                label={{ 
                  value: 'Fall Risk (%)', 
                  angle: -90, 
                  position: 'insideLeft',
                  offset: -45,
                  style: { textAnchor: 'middle' }
                }} 
              />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Line type="monotone" dataKey="risk" name="Fall Risk by Age Group" stroke="#e57373" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={500}>
            <RadarChart cx="50%" cy="50%" outerRadius={150} data={conditionRiskData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="name" />
              <PolarRadiusAxis angle={30} domain={[0, 100]} />
              <Radar name="Mobility" dataKey="mobility" stroke="#e57373" fill="#e57373" fillOpacity={0.6} />
              <Radar name="Balance" dataKey="balance" stroke="#64b5f6" fill="#64b5f6" fillOpacity={0.6} />
              <Radar name="Strength" dataKey="strength" stroke="#4db6ac" fill="#4db6ac" fillOpacity={0.6} />
              <Radar name="Cognition" dataKey="cognition" stroke="#9575cd" fill="#9575cd" fillOpacity={0.6} />
              <Radar name="Vision" dataKey="vision" stroke="#ffb74d" fill="#ffb74d" fillOpacity={0.6} />
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        );
      
      case 'multiBar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={[
                { category: 'Intrinsic Factors', value: 65, fill: '#e57373' },
                { category: 'Environmental Hazards', value: 55, fill: '#64b5f6' },
                { category: 'Medication Risks', value: 45, fill: '#4db6ac' },
                { category: 'Behavioral Factors', value: 35, fill: '#9575cd' },
                { category: 'Functional Limitations', value: 60, fill: '#ffb74d' }
              ]}
              margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis 
                label={{ 
                  value: 'Contribution to Fall Risk (%)', 
                  angle: -90, 
                  position: 'insideLeft',
                  offset: -45,
                  style: { textAnchor: 'middle' }
                }} 
              />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey="value" name="Contribution to Fall Risk" fill="#8884d8">
                {fallRiskFactorsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <Typography color="error">
            Chart type '{chartType}' not supported
          </Typography>
        );
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
        {title || "Fall Risk Assessment Data"}
      </Typography>
      
      {description && (
        <Typography variant="body1" sx={{ mb: 3, color: '#555' }}>
          {description}
        </Typography>
      )}
      
      <Box sx={{ mt: 2 }}>
        {renderChart()}
      </Box>
      
      <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'right', color: 'text.secondary' }}>
        Data based on research from National Council on Aging and CDC STEADI program
      </Typography>
    </Paper>
  );
};

export default FallRiskCharts;
